import { lazy } from 'react';
import type { ReactElement } from 'react';
import type { RouteObject } from 'react-router-dom';
import { RequireFeature } from '@/features/authentication';
import { LazyLoad } from '@/shared/components/LazyLoad';
import type { FeatureAction } from '@/core/security';
import { RFQ_FEATURE } from './bids/models/rfq.constants';
import { RFQ_ROUTE_ACTION } from './bids/models/rfqAccess';
import { BuyerBidsGate } from './bids/components/BidsRoleGate';

const RfqListPage = lazy(() => import('./bids/pages/RfqListPage'));
const RfqTemplatePickerPage = lazy(() => import('./bids/pages/RfqTemplatePickerPage'));
const RfqWizardPage = lazy(() => import('./bids/pages/RfqWizardPage'));
const RfqDetailPage = lazy(() => import('./bids/pages/RfqDetailPage'));
const RfqBidComparisonPage = lazy(() => import('./bids/pages/RfqBidComparisonPage'));

/**
 * The BID screens behind the feature the RBAC matrix scores them against -- the same grant the sidebar
 * entry (`feature: 'Request For Quotation'`) filters on, so a hidden menu is also a closed URL. A route that writes
 * also needs its action (`RFQ_ROUTE_ACTION`): a new BID needs CREATE and editing a draft needs UPDATE -- the same
 * actions the list offers Create and Edit by -- so a typed URL cannot open a form whose every save the platform
 * would refuse.
 */
function guarded(element: ReactElement, action?: FeatureAction): RouteObject['element'] {
  const page = <LazyLoad>{element}</LazyLoad>;
  return (
    <RequireFeature feature={RFQ_FEATURE}>
      {action ? (
        <RequireFeature feature={RFQ_FEATURE} action={action}>
          {page}
        </RequireFeature>
      ) : (
        page
      )}
    </RequireFeature>
  );
}

/**
 * Relative paths: `app/routes/protectedRoutes.tsx` nests these inside the Layout branch.
 *
 * `new` and `templates` are declared before `:id` so they are never read as a record id (React Router
 * ranks static segments first anyway; the order just says so).
 *
 * The buyer's BIDs only. The supplier's side (`/supplier-bids`) is the `supplier/supplier-quotation` feature's (2026-10-28).
 */
export const rfqRoutes: RouteObject[] = [
  {
    /* The buyer's BIDs; a Supplier-role session is sent to its own (2026-10-27). */
    path: 'rfqs',
    element: <BuyerBidsGate />,
    children: [
      { index: true, element: guarded(<RfqListPage />) },
      { path: 'templates', element: guarded(<RfqTemplatePickerPage />) },
      { path: 'new', element: guarded(<RfqWizardPage />, RFQ_ROUTE_ACTION.create) },
      { path: ':id/edit', element: guarded(<RfqWizardPage />, RFQ_ROUTE_ACTION.edit) },
      /* Bid Comparison (2026-10-28): read access, as the BID itself. */
      { path: ':id/compare', element: guarded(<RfqBidComparisonPage />) },
      { path: ':id', element: guarded(<RfqDetailPage />) },
    ],
  },
];
